import { Client } from "colyseus.js";
import { manageRoom, type ConnectionRegistry, type ManagedConnectionStatus } from "./lifecycle";
import { watchJoin } from "./join-watch";

/** SHA-256 (lowercase hex) of the DM members string, via the browser's Web Crypto — the opaque
 *  matchmaking key. Matches the server's node:crypto `dmRoomKey` (both standard SHA-256), so both
 *  peers rendezvous on the same key without the raw pair ever appearing in the matchmake listing. */
async function dmRoomKey(members: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(members));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** A single direct message, as persisted + relayed by the game-server `dm` room. */
export interface DmMessage {
  id: string;
  /** App user id (cuid) of the sender — the `dm` room keys users on the app id. */
  senderId: string;
  body: string;
  at: number;
  /** Discriminator: a normal text message, or a voice note (see the voice fields). */
  kind: "text" | "voice";
  /** For `kind: "voice"`: the uploaded note's id (download on demand to play). */
  voiceId?: string;
  /** For `kind: "voice"`: the clip length in ms, for the bubble's mm:ss + progress. */
  voiceDurationMs?: number;
  /** Sender display name — only the multi-party TABLE chat sets it (so the shared
   * message list can caption incoming bubbles); real DMs never carry it. */
  senderName?: string;
}

/** The wire shape: older/text messages may omit `kind` (defaulted to `"text"`). */
type IncomingDm = Omit<DmMessage, "kind"> & { kind?: "text" | "voice" };
const normalize = (m: IncomingDm): DmMessage => ({ ...m, kind: m.kind ?? "text" });

/** Typed send-rejection code relayed by the DM room's `dm-error` message. The
 * store keeps it verbatim in `error`, so the screen can show the REAL reason
 * (`"BLOCKED"` — a block in either direction forbids the conversation). */
export const DM_BLOCKED_CODE = "BLOCKED";

export interface DmHandlers {
  /** The full thread as served on (re)join — the store REPLACES its list
   * wholesale with this payload (a rejoin's history covers any dead-window gap). */
  onHistory: (messages: DmMessage[]) => void;
  onMessage: (message: DmMessage) => void;
  onError: (message: string) => void;
  /** The lifecycle's reconnect state for THIS chat socket (reconnecting /
   * connected / failed) — drives the chat surfaces' own inline notice; chat is
   * registered `silent`, so it never touches the full-screen game overlay.
   * Optional so minimal fake connectors in tests stay valid. */
  onConnection?: (status: ManagedConnectionStatus) => void;
}

export interface DmRoomHandle {
  say: (body: string) => void;
  /** Send a voice note (already uploaded) into the conversation. */
  sayVoice: (voiceId: string, durationMs: number) => void;
  /** Restart the reconnect cycle after `failed` (the «تلاش دوباره» button).
   * Optional so minimal fakes stay valid. */
  retry?: () => void;
  leave: () => void;
}

/**
 * Opens a DM conversation and wires the handlers. Abstracted (like the Hearts
 * connector) so the chat store is testable with a fake connector — no WebSocket.
 */
export type DmConnector = (handlers: DmHandlers) => Promise<DmRoomHandle>;

/** Deterministic conversation id: the sorted **app user-id** pair, `a:b`. Both
 * peers compute the same string, so matchmaking's `filterBy(["conversationId"])`
 * lands them in the same room regardless of who opens it first. Keyed on the app
 * id (never the Telegram id) so it matches the DM room + DB foreign keys. */
export function conversationId(selfUserId: string, peerUserId: string): string {
  return [selfUserId, peerUserId].sort().join(":");
}

/** The real connector: joins the Colyseus `dm` room over the game-server WS,
 * managed by the app lifecycle exactly like the game sockets (probe on resume,
 * backoff rejoin on a dead socket). */
export function createColyseusDmConnector(opts: {
  wsUrl: string;
  conversationId: string;
  getInitData: () => string | null;
  /** Lifecycle registry (injected in tests; defaults to the app singleton). */
  registry?: ConnectionRegistry;
}): DmConnector {
  return async (handlers) => {
    const client = new Client(opts.wsUrl);
    // The matchmaking filter key is the OPAQUE hash of the pair — so the public matchmake listing
    // can't reveal who is DMing whom — while the raw pair rides as `members` (a non-filter option
    // the server uses for participants + verifies against the key). Computed once; it's fixed for
    // the conversation.
    const roomKey = await dmRoomKey(opts.conversationId);
    const joinOptions = (): Record<string, unknown> => ({
      initData: opts.getInitData() ?? "",
      conversationId: roomKey,
      members: opts.conversationId,
    });

    const managed = manageRoom({
      name: "dm",
      registry: opts.registry,
      // Chat is NOT a game seat: it reconnects SILENTLY as far as the full-screen
      // game ConnectionOverlay is concerned (that overlay belongs to game sockets
      // only) — the thread surfaces its own inline state via `onConnection`.
      silent: true,
      onStatus: (status) => handlers.onConnection?.(status),
      initial: await watchJoin(
        { conn: "dm", kind: "dm" },
        client.joinOrCreate("dm", joinOptions()),
      ),
      // Reconnect = the NORMAL join: `filterBy(["conversationId"])` lands the
      // fresh socket back in the same conversation, and the DM room's onJoin
      // re-serves the last-50 history (the store replaces its list wholesale,
      // covering messages that arrived while the socket was dead) and re-marks
      // the open thread read.
      reacquire: () => client.joinOrCreate("dm", joinOptions()),
      wire: (room) => {
        room.onMessage("history", (messages: IncomingDm[]) =>
          handlers.onHistory(messages.map(normalize)),
        );
        room.onMessage("dm", (message: IncomingDm) => handlers.onMessage(normalize(message)));
        // A refused send (typed by the DmService): surface the code through the SAME
        // error path — the store flips to its error state and the screen maps
        // `"BLOCKED"` to the real Persian reason.
        room.onMessage("dm-error", (payload: { code?: string }) =>
          handlers.onError(typeof payload?.code === "string" ? payload.code : "INVALID"),
        );
        // Transport drop (no server message) → the lifecycle's silent reconnect
        // (onStatus) owns it; don't flip the thread to an error state. Only a real
        // server-sent message routes to the error path.
        room.onError((code: number, message?: string) => {
          if (message) handlers.onError(message);
        });
      },
    });

    return {
      say: (body) => managed.send("say", { body }),
      sayVoice: (voiceId, durationMs) => managed.send("say-voice", { voiceId, durationMs }),
      retry: () => managed.retry(),
      leave: () => managed.leave(),
    };
  };
}
